import type { BookingEntry, Segment } from '@nono/contract';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Target } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BookingHoverCard } from '@/components/bookings/BookingHoverCard';
import { BookingList } from '@/components/bookings/BookingList';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { useBookings } from '@/lib/queries';

const MESSAGES = {
  nb: {
    ad: 'Annonse',
    spend: 'Forbruk',
    clicks: 'Klikk',
    bookings: 'Bookinger',
    costBooking: 'Kost/booking',
    none: 'Ingen annonsedata for denne dagen.',
    unknownCampaign: 'Ukjent kampanje',
    unknownAdset: 'Ukjent annonsesett',
    tagCampaign: 'Kampanje',
    tagAdset: 'Annonsesett',
    recon: (sched: string, momence: string) =>
      `${sched} nettside-bookinger tilskrevet av Meta · ${momence} bookinger registrert i Momence`,
    unattributed: (n: string) => ` · ${n} ikke tilskrevet en annonse (organisk/uten match)`,
    headerEqual: (n: string) => `Bookinger (${n})`,
    headerDiff: (meta: string, named: string) =>
      `Meta: ${meta} · navngitt av vår sporing: ${named}`,
    noneNamed: 'Ingen navngitte bookinger matchet denne annonsen i perioden.',
  },
  en: {
    ad: 'Ad',
    spend: 'Spend',
    clicks: 'Clicks',
    bookings: 'Bookings',
    costBooking: 'Cost/booking',
    none: 'No ad data for this day.',
    unknownCampaign: 'Unknown campaign',
    unknownAdset: 'Unknown ad set',
    tagCampaign: 'Campaign',
    tagAdset: 'Ad set',
    recon: (sched: string, momence: string) =>
      `${sched} website bookings attributed by Meta · ${momence} bookings recorded in Momence`,
    unattributed: (n: string) => ` · ${n} not attributed to an ad (organic/unmatched)`,
    headerEqual: (n: string) => `Bookings (${n})`,
    headerDiff: (meta: string, named: string) => `Meta: ${meta} · named by our tracking: ${named}`,
    noneNamed: 'No named bookings matched this ad in this range.',
  },
};

type Msgs = (typeof MESSAGES)['nb'];
type AdRow = Awaited<ReturnType<typeof api.ads>>['ads'][number];

const NO_BOOKINGS: BookingEntry[] = [];

type Agg = { spend: number; clicks: number; bookings: number };
type AdsetNode = { id: string; name: string; ads: AdRow[]; agg: Agg };
type CampaignNode = { id: string; name: string; adsets: AdsetNode[]; agg: Agg };

function aggregate(ads: AdRow[]): Agg {
  return ads.reduce(
    (s, a) => ({
      spend: s.spend + a.spend,
      clicks: s.clicks + a.clicks,
      bookings: s.bookings + a.bookings,
    }),
    { spend: 0, clicks: 0, bookings: 0 },
  );
}

/** Build the campaign → ad set → ad tree, each node carrying its own subtotal.
 *  Ads with no campaign/adset identity (pre-backfill) fall under an "unknown"
 *  group rather than disappearing. Sorted by bookings then spend at every level. */
function buildTree(ads: AdRow[], m: Msgs): CampaignNode[] {
  const campaigns = new Map<string, { id: string; name: string; adsets: Map<string, AdsetNode> }>();
  for (const a of ads) {
    const cid = a.campaignId ?? '__none';
    const aid = a.adsetId ?? '__none';
    let c = campaigns.get(cid);
    if (!c) {
      c = { id: cid, name: a.campaignName ?? m.unknownCampaign, adsets: new Map() };
      campaigns.set(cid, c);
    }
    let s = c.adsets.get(aid);
    if (!s) {
      s = {
        id: aid,
        name: a.adsetName ?? m.unknownAdset,
        ads: [],
        agg: { spend: 0, clicks: 0, bookings: 0 },
      };
      c.adsets.set(aid, s);
    }
    s.ads.push(a);
  }
  const byBookingsThenSpend = (x: Agg, y: Agg) => y.bookings - x.bookings || y.spend - x.spend;
  return [...campaigns.values()]
    .map((c) => {
      const adsets = [...c.adsets.values()]
        .map((s) => ({ ...s, agg: aggregate(s.ads) }))
        .sort((x, y) => byBookingsThenSpend(x.agg, y.agg));
      for (const s of adsets) s.ads.sort((x, y) => y.bookings - x.bookings || y.spend - x.spend);
      return { id: c.id, name: c.name, adsets, agg: aggregate(adsets.flatMap((s) => s.ads)) };
    })
    .sort((x, y) => byBookingsThenSpend(x.agg, y.agg));
}

const costPer = (a: Agg) => (a.bookings > 0 ? a.spend / a.bookings : null);

export function DayAds({
  date,
  segment,
  dayBookings,
}: {
  date: string;
  segment: Segment;
  dayBookings: number | null;
}) {
  const m = useViewMessages(MESSAGES);
  const q = useQuery({
    queryKey: ['ads', { from: date, to: date, segment, level: 'ad' as const }],
    queryFn: () => api.ads({ from: date, to: date, segment, level: 'ad' }),
  });
  const bq = useBookings(date, date);
  const namedByAd = useMemo(() => {
    const map = new Map<string, BookingEntry[]>();
    for (const b of bq.data?.bookings ?? []) {
      if (b.date !== date || !b.ad?.adId) continue;
      const arr = map.get(b.ad.adId);
      if (arr) arr.push(b);
      else map.set(b.ad.adId, [b]);
    }
    return map;
  }, [bq.data, date]);

  const tree = useMemo(() => (q.data ? buildTree(q.data.ads, m) : []), [q.data, m]);
  // Default: everything expanded so the full campaign → ad set → ad hierarchy
  // is visible at a glance; the visitor can collapse any branch.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (q.isLoading)
    return (
      <div className="flex justify-center py-3">
        <Spinner />
      </div>
    );
  if (q.isError || !q.data || q.data.ads.length === 0) return <EmptyState title={m.none} />;

  const sched = q.data.scheduleTotal;
  const unattributed = dayBookings !== null && dayBookings > sched ? dayBookings - sched : 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[440px] table-fixed text-label">
          <colgroup>
            <col style={{ width: '56%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
          </colgroup>
          <thead>
            <tr className="text-fg-subtle">
              <th className="py-1 text-left font-medium">{m.ad}</th>
              <th className="py-1 text-right font-medium">{m.spend}</th>
              <th className="py-1 text-right font-medium">{m.clicks}</th>
              <th className="py-1 text-right font-medium">{m.bookings}</th>
              <th className="py-1 text-right font-medium">{m.costBooking}</th>
            </tr>
          </thead>
          <tbody>
            {tree.map((c) => {
              const cKey = `c:${c.id}`;
              const cOpen = !collapsed.has(cKey);
              return (
                <Fragmentish key={cKey}>
                  <TreeRow
                    level={0}
                    open={cOpen}
                    hasChildren={c.adsets.length > 0}
                    onToggle={() => toggle(cKey)}
                    name={c.name}
                    agg={c.agg}
                  />
                  {cOpen &&
                    c.adsets.map((s) => {
                      const sKey = `s:${c.id}:${s.id}`;
                      const sOpen = !collapsed.has(sKey);
                      return (
                        <Fragmentish key={sKey}>
                          <TreeRow
                            level={1}
                            open={sOpen}
                            hasChildren={s.ads.length > 0}
                            onToggle={() => toggle(sKey)}
                            name={s.name}
                            agg={s.agg}
                          />
                          {sOpen &&
                            s.ads.map((a) => (
                              <tr key={a.entityId}>
                                <td
                                  className="truncate py-1 pl-7 text-fg-subtle sm:pl-11"
                                  title={a.name}
                                >
                                  {a.name}
                                </td>
                                <td className="py-1 text-right text-fg-subtle tabular">
                                  {formatMetric(a.spend, 'nok')}
                                </td>
                                <td className="py-1 text-right text-fg-subtle tabular">
                                  {formatMetric(a.clicks, 'int')}
                                </td>
                                <td className="py-1 text-right text-fg-muted tabular">
                                  <AdBookingsCell
                                    m={m}
                                    metaBookings={a.bookings}
                                    named={namedByAd.get(a.entityId) ?? NO_BOOKINGS}
                                    loaded={Boolean(bq.data)}
                                  />
                                </td>
                                <td className="py-1 text-right text-fg-subtle tabular">
                                  {formatMetric(a.cpBooking, 'nok')}
                                </td>
                              </tr>
                            ))}
                        </Fragmentish>
                      );
                    })}
                </Fragmentish>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="flex items-start gap-1.5 text-micro text-fg-subtle">
        <Target className="size-3 shrink-0 text-accent-booking" aria-hidden />
        {m.recon(formatMetric(sched, 'int'), formatMetric(dayBookings, 'int'))}
        {unattributed > 0 ? m.unattributed(formatMetric(unattributed, 'int')) : ''}
      </p>
    </div>
  );
}

/** A campaign (level 0) or ad-set (level 1) grouping row. Visually distinct so
 *  the eye can tell the levels apart at a glance: the campaign is a bold, brand-
 *  tinted band; the ad set is a lighter, indented sub-band; the ads (rendered by
 *  the caller) are muted and indented deepest. */
function TreeRow({
  level,
  open,
  hasChildren,
  onToggle,
  name,
  agg,
}: {
  level: 0 | 1;
  open: boolean;
  hasChildren: boolean;
  onToggle: () => void;
  name: string;
  agg: Agg;
}) {
  const cost = costPer(agg);
  const isCampaign = level === 0;
  // Differentiation comes from weight + colour + indentation, not decoration.
  // Campaign: bold, full-colour, a faint band + a stronger top border that reads
  // as a gap between groups. Ad set: medium, muted, indented, no band/border so
  // rows inside a group stay calm.
  const metric = isCampaign ? 'font-semibold text-fg' : 'text-fg-muted';
  const pad = isCampaign ? 'py-2' : 'py-1.5';
  return (
    <tr className={isCampaign ? 'border-t-2 border-border-strong bg-surface-2' : ''}>
      <td className={`truncate ${pad}`} style={{ paddingLeft: isCampaign ? 4 : 26 }} title={name}>
        <button
          type="button"
          onClick={onToggle}
          className="flex min-h-10 w-full min-w-0 items-center gap-1.5 text-left sm:min-h-0"
          aria-expanded={open}
        >
          {hasChildren ? (
            open ? (
              <ChevronDown className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
            ) : (
              <ChevronRight className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
            )
          ) : (
            <span className="inline-block size-3.5 shrink-0" />
          )}
          <span
            className={
              isCampaign ? 'truncate font-semibold text-fg' : 'truncate font-medium text-fg-muted'
            }
          >
            {name}
          </span>
        </button>
      </td>
      <td className={`${pad} text-right tabular ${metric}`}>{formatMetric(agg.spend, 'nok')}</td>
      <td className={`${pad} text-right tabular ${metric}`}>{formatMetric(agg.clicks, 'int')}</td>
      <td className={`${pad} text-right tabular ${metric}`}>{formatMetric(agg.bookings, 'int')}</td>
      <td className={`${pad} text-right tabular ${metric}`}>{formatMetric(cost, 'nok')}</td>
    </tr>
  );
}

/** Lets us return sibling <tr>s without a wrapper that breaks table semantics. */
function Fragmentish({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/** The bookings count for one ad row — hover/click names WHO booked via this ad,
 *  with an honesty header when Meta's count and our named count disagree. */
function AdBookingsCell({
  m,
  metaBookings,
  named,
  loaded,
}: {
  m: Msgs;
  metaBookings: number;
  named: BookingEntry[];
  loaded: boolean;
}) {
  const count = formatMetric(metaBookings, 'int');
  if (metaBookings === 0 && named.length === 0) return <>{count}</>;
  const differs = metaBookings !== named.length;
  return (
    <BookingHoverCard
      className="inline-block decoration-fg-subtle decoration-dotted underline underline-offset-2"
      card={
        loaded ? (
          <>
            <p className="text-label font-medium text-fg">
              {differs
                ? m.headerDiff(count, formatMetric(named.length, 'int'))
                : m.headerEqual(formatMetric(named.length, 'int'))}
            </p>
            <div className="mt-1.5">
              <BookingList bookings={named} emptyText={m.noneNamed} />
            </div>
          </>
        ) : null
      }
    >
      {count}
    </BookingHoverCard>
  );
}
